import { useEffect, useRef, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";

import NoResultsFound from "../../common/components/noResultsFound/NoResultsFound";
import UserDropdownItem from "../users/UserDropdownItem";
import { User } from "../users/User";
import { fetchUser, quickSearchUsers } from "../users/usersApi";
import { PropsWithClassName } from "../../common/types/PropsWithClassName";
import FormGroup from "../../common/components/form/FormGroup";

interface ApproverDropdownProps extends PropsWithClassName {
  showClear?: boolean;
  disabled?: boolean;
  approverId?: string;
  approverEmailAddress?: string;
  onChangeApproverId?: (approverId: string) => void;
  onChangeApproverEmailAddress?: (approverEmailAddress: string) => void;
}

export default function ApproverDropdown({
  className,
  showClear,
  disabled,
  approverId,
  approverEmailAddress,
  onChangeApproverId,
  onChangeApproverEmailAddress,
}: ApproverDropdownProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [approvers, setApprovers] = useState<User[]>([]);
  const [selectedApprover, setSelectedApprover] = useState<User>();
  const [selectedApproverEmailAddress, setSelectedApproverEmailAddress] = useState(approverEmailAddress || "");
  const [searchTerm, setSearchTerm] = useState("");
  const options = approvers.length ? approvers : selectedApprover ? [selectedApprover] : [];

  useEffect(() => {
    fetchSelectedApprover();
  }, []);

  useEffect(() => {
    const selectedApprover = approvers.find((approver) => approver.id === approverId);
    setSelectedApprover(selectedApprover);
  }, [JSON.stringify(approvers), approverId]);

  useEffect(() => {
    const timeoutId = setTimeout(() => doQuickSearchApprovers(), 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const doQuickSearchApprovers = async () => {
    if (searchTerm) {
      const approvers = await quickSearchUsers({ searchTerm, canApproveTickets: true });
      const shouldAddSelectedApprover = selectedApprover && !approvers.some((approver) => approver.id === selectedApprover.id);
      setApprovers(shouldAddSelectedApprover ? [selectedApprover, ...approvers] : approvers);
    }
  };

  const fetchSelectedApprover = async () => {
    if (approverId) {
      const approver = await fetchUser(approverId);
      setSelectedApprover(approver);
    }
  };

  return (
    <div className="flex flex-column">
      <FormGroup noMargin label="Trimite email manager">
        <div className="flex gap-2">
          <InputText
            type="email"
            className="flex-1"
            placeholder="Adauga email manager"
            value={selectedApproverEmailAddress}
            onChange={(e) => setSelectedApproverEmailAddress(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && selectedApproverEmailAddress) {
                onChangeApproverEmailAddress?.(selectedApproverEmailAddress);
              }
            }}
          />
          <Button
            icon="pi pi-send"
            onClick={() => {
              if (selectedApproverEmailAddress) {
                onChangeApproverEmailAddress?.(selectedApproverEmailAddress);
              }
            }}
          />
        </div>
      </FormGroup>
      <Divider align="center">
        <span>SAU</span>
      </Divider>
      <FormGroup noMargin label="Selecteaza manager">
        <Dropdown
          panelClassName="w-25rem"
          className={`${className} w-full`}
          placeholder="Selecteaza manager"
          options={options}
          optionLabel="displayName"
          value={selectedApprover}
          itemTemplate={(user: User) => <UserDropdownItem user={user} />}
          filter
          filterTemplate={
            <InputText
              ref={inputRef}
              className="w-full"
              placeholder="Cauta manager"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          }
          showClear={showClear}
          disabled={disabled}
          emptyMessage={<NoResultsFound />}
          onShow={() => inputRef.current?.focus({ preventScroll: true })}
          onChange={(e) => onChangeApproverId?.(e.value?.id)}
        />
      </FormGroup>
    </div>
  );
}
